import React from 'react';
import { palette, typography, spacing, components, withOpacity } from '../../utils/designTokens';
import AnomalyShapeRadars from './AnomalyShapeRadars';

// Business Task Drift evidence: how a task's recent runs sit against a baseline pinned at an
// earlier date (the anchor), and where the rolling baseline has moved in the meantime.
// Input comes from getMockDriftEvidence(); only the demo produces it today.

const FORMATS = {
  ratio: v => v.toFixed(2),
  count: v => v.toFixed(1),
  seconds: v => `${(v / 1000).toFixed(1)}s`,
  share: v => v.toFixed(3),
};

const formatZ = (z) => `${z > 0 ? '+' : z < 0 ? '−' : '±'}${Math.abs(z).toFixed(1)}σ`;

const CHART = { w: 300, h: 150, left: 40, right: 10, top: 12, bottom: 26 };

function TrendChart({ series, weeks }) {
  const format = FORMATS[series.format] || FORMATS.count;
  const { values, rolling, mean, std } = series;
  const last = values[values.length - 1];
  const lo = Math.min(mean - 2.4 * std, ...values);
  const hi = Math.max(mean + 2.4 * std, ...values);
  const pad = (hi - lo) * 0.06;
  const y = v => CHART.top + (CHART.h - CHART.top - CHART.bottom) * (1 - (v - (lo - pad)) / (hi - lo + 2 * pad));
  const x = i => CHART.left + (CHART.w - CHART.left - CHART.right) * (i / (values.length - 1));
  const path = arr => arr.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  const anchorColor = palette.accents.secondary.base;
  const runColor = palette.severity.medium.text;
  const tickWeeks = [0, Math.floor((values.length - 1) / 2), values.length - 1];

  return (
    <div className={`${components.section.subsection.bg} ${components.section.subsection.border} ${components.section.subsection.rounded} ${components.section.subsection.padding} min-w-0`}>
      <div className={`${typography.size.sm} ${typography.weight.semibold}`} style={{ color: palette.text.primary }}>{series.title}</div>
      <div className={typography.size.xs} style={{ color: palette.text.muted }}>
        {format(values[0])} → {format(last)} ·{' '}
        <span className={typography.family.mono} style={{ color: runColor }}>{formatZ((last - mean) / std)}</span> vs anchor
      </div>
      <svg viewBox={`0 0 ${CHART.w} ${CHART.h}`} className="mt-1 block h-auto w-full overflow-visible" role="img"
        aria-label={`${series.title}, weekly median against the anchored range`}>
        <rect x={CHART.left} y={y(mean + 2 * std)} width={CHART.w - CHART.left - CHART.right}
          height={y(mean - 2 * std) - y(mean + 2 * std)} fill={withOpacity(anchorColor, 12)} />
        {[mean - 2 * std, mean, mean + 2 * std].map(v => (
          <g key={v}>
            <line x1={CHART.left} x2={CHART.w - CHART.right} y1={y(v)} y2={y(v)}
              stroke={v === mean ? anchorColor : palette.support.gridline}
              strokeWidth={v === mean ? 1.5 : 1} strokeDasharray={v === mean ? '5 4' : undefined} />
            <text x={CHART.left - 5} y={y(v) + 3} textAnchor="end"
              className={`${typography.size.metadata} ${typography.family.mono}`} fill={palette.text.muted}>{format(v)}</text>
          </g>
        ))}
        {tickWeeks.map((i, k) => (
          <text key={i} x={x(i)} y={CHART.h - 8} textAnchor={k === 0 ? 'start' : k === tickWeeks.length - 1 ? 'end' : 'middle'}
            className={typography.size.metadata} fill={palette.text.muted}>{weeks[i]}</text>
        ))}
        <path d={path(rolling)} fill="none" stroke={palette.text.supporting} strokeWidth={2} strokeDasharray="1.5 4" strokeLinecap="round" />
        <path d={path(values)} fill="none" stroke={runColor} strokeWidth={2} strokeLinejoin="round" />
        {values.map((v, i) => (
          <g key={weeks[i]}>
            <circle cx={x(i)} cy={y(v)} r={i === values.length - 1 ? 4 : 2.6} fill={runColor} />
            <rect x={x(i) - 16} y={CHART.top} width={32} height={CHART.h - CHART.top - CHART.bottom} fill="transparent">
              <title>{`${weeks[i]}: ${format(v)} (${formatZ((v - mean) / std)} vs anchor, ${formatZ((v - rolling[i]) / std)} vs rolling)`}</title>
            </rect>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function DriftEvidence({ evidence }) {
  if (!evidence || !Array.isArray(evidence.thisWeek) || evidence.thisWeek.length === 0) return null;
  const { facts = [], weeks = [], series = [] } = evidence;

  return (
    <div className={`flex flex-col ${spacing.gap.lg}`}>
      {facts.length > 0 && (
        <div className={`grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] ${spacing.gap.md}`}>
          {facts.map(fact => (
            <div key={fact.label}
              className={`${components.section.subsection.bg} ${components.section.subsection.border} ${components.section.subsection.rounded} ${components.section.subsection.padding}`}>
              <div className={`${typography.size.label} uppercase tracking-wide`} style={{ color: palette.text.muted }}>{fact.label}</div>
              <div className={`${typography.size.base} ${typography.weight.medium} ${typography.family.mono}`} style={{ color: palette.text.heading }}>{fact.value}</div>
              <div className={typography.size.xs} style={{ color: palette.text.supporting }}>{fact.note}</div>
            </div>
          ))}
        </div>
      )}

      <AnomalyShapeRadars
        runs={[{ name: 'This week, median run', all_deviations: evidence.thisWeek }]}
        rolling={evidence.rolling}
        labels={{
          run: 'This week, median run',
          baseline: evidence.anchorLabel || 'Anchored baseline',
          rolling: 'Rolling baseline (current model)',
          workloadQuestion: "How heavy and costly were this week's runs?",
          executionQuestion: 'Did they run the usual mix of steps?',
        }}
      />

      {series.length > 0 && weeks.length > 1 && (
        <div className={`flex flex-col ${spacing.gap.sm}`}>
          <div className={`${typography.size.xs} ${typography.weight.semibold} uppercase tracking-wide`} style={{ color: palette.text.supporting }}>
            Weekly median, {weeks[0]} – {weeks[weeks.length - 1]}
          </div>
          <div className={`grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] ${spacing.gap.md}`}>
            {series.map(s => <TrendChart key={s.title} series={s} weeks={weeks} />)}
          </div>
        </div>
      )}
    </div>
  );
}
